import { t } from "../i18n";

/** Fallback hub (Addis) only when route has no stored pickup and geolocation is not used. */
export const DEFAULT_MERCHANT_HUB = { lat: 9.0331, lng: 38.7621 };

/** Whether map legs should return to pickup (round trip). */
export function isRoundTripTour(result) {
  return result?.tourMode !== "ONE_WAY";
}

/**
 * Resolves [lat, lng] for map legs and hub marker from API result.
 * Order: pickupLat/pickupLng on DTO → hub inside qrPayload JSON → default.
 */
export function hubCoordsFromRouteResult(result) {
  if (result == null) {
    return [DEFAULT_MERCHANT_HUB.lat, DEFAULT_MERCHANT_HUB.lng];
  }
  const plat =
    result.pickupLat != null ? Number(result.pickupLat) : Number(result.pickupLatitude);
  const plng =
    result.pickupLng != null ? Number(result.pickupLng) : Number(result.pickupLongitude);
  if (
    plat != null &&
    plng != null &&
    !Number.isNaN(plat) &&
    !Number.isNaN(plng)
  ) {
    return [plat, plng];
  }
  try {
    const raw = result.qrPayload ?? result.qrCode;
    if (typeof raw === "string" && raw.trim()) {
      const qr = JSON.parse(raw);
      const hLat = qr?.hubLat != null ? Number(qr.hubLat) : null;
      const hLng = qr?.hubLng != null ? Number(qr.hubLng) : null;
      if (
        hLat != null &&
        hLng != null &&
        !Number.isNaN(hLat) &&
        !Number.isNaN(hLng)
      ) {
        return [hLat, hLng];
      }
    }
  } catch (_) {
    // ignore
  }
  return [DEFAULT_MERCHANT_HUB.lat, DEFAULT_MERCHANT_HUB.lng];
}

/**
 * Friendly text for a browser geolocation failure (code 1 = denied, 2 = unavailable, 3 = timeout).
 */
export function describeLocationError(err) {
  if (typeof window !== "undefined" && window.isSecureContext === false) {
    return t("location.insecure");
  }
  switch (err?.code) {
    case 1:
      return t("location.denied");
    case 2:
      return t("location.unavailable");
    case 3:
      return t("location.timeout");
    default:
      return err?.message || t("location.unknown");
  }
}

/**
 * Current browser position (optional pickup override). Rejects with a user-friendly Error when it isn't available.
 */
export function getBrowserPickupLocation() {
  if (!navigator.geolocation) {
    return Promise.reject(new Error("This browser can’t share its location. Use your shop location instead."));
  }
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      (err) => {
        const e = new Error(describeLocationError(err));
        e.code = err?.code;
        reject(e);
      },
      {
        // Low accuracy uses Wi-Fi / network location, which works on laptops without GPS.
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 5 * 60 * 1000,
      }
    );
  });
}
